import React, { Component } from 'react';
import { X, Send, Reply, Paperclip, ThumbsUp, ThumbsDown } from 'lucide-react';
import { apiService } from '@/services/api';
import { tournamentChatWebSocketService } from '@/services/tournamentChatWebSocket';
import { getUserIdFromToken } from '@/utils/jwt';
import { cleanUUID, isValidUUID } from '@/utils/uuid';
import { linkifyText } from '@/utils/linkify';
import styles from './TournamentGroupChat.module.scss';

/** If URL is YouTube or Vimeo, return embed URL so it can play inside the chat. */
function getVideoEmbedUrl(url: string): string | null {
  if (!url || typeof url !== 'string') return null;
  const s = url.trim();
  const ytMatch = s.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]+)/i);
  if (ytMatch) return `https://www.youtube.com/embed/${ytMatch[1]}?autoplay=0`;
  const vimeoMatch = s.match(/vimeo\.com\/(?:video\/)?(\d+)/i);
  if (vimeoMatch) return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
  return null;
}

interface TournamentGroupChatProps {
  tournamentId: string;
  tournamentName: string;
  participantCount?: number;
  onClose: () => void;
}

interface ReplyToMessage {
  id: string;
  messageText: string;
  senderId: string;
  senderName: string;
  createdAt: string;
}

interface MessageReactions {
  like: string[];
  dislike: string[];
}

interface GroupMessage {
  id: string;
  senderId: string;
  senderName: string;
  message: string;
  timestamp: string;
  replyToMessage?: ReplyToMessage | null;
  attachments?: { id: string; fileType: string; fileUrl: string; fileName?: string | null }[];
  reactions: MessageReactions;
}

interface TournamentGroupChatState {
  conversationId: string | null;
  messages: GroupMessage[];
  inputMessage: string;
  isLoading: boolean;
  isSending: boolean;
  isUploading: boolean;
  error: string | null;
  connectionStatus: 'connecting' | 'connected' | 'disconnected' | 'error';
  chatAvailable: boolean;
  replyingTo: GroupMessage | null;
  selectedFiles: File[];
  typingUserIds: Set<string>;
  participantNames: Record<string, string>;
}

function normalizeReactions(reactions: any[] | undefined): MessageReactions {
  const like: string[] = [];
  const dislike: string[] = [];
  if (Array.isArray(reactions)) {
    reactions.forEach((r: any) => {
      const uid = r.userId || r.user_id;
      if (!uid) return;
      if (r.reactionType === 'like') like.push(uid);
      else if (r.reactionType === 'dislike') dislike.push(uid);
    });
  }
  return { like, dislike };
}

function mapApiMessageToGroupMessage(msg: any, currentUserId: string, participantNames: Record<string, string>): GroupMessage {
  const senderId = msg.senderId || msg.sender?.id;
  const senderName = msg.sender?.displayName ?? msg.sender?.identifier ?? participantNames[senderId] ?? (senderId === currentUserId ? 'You' : 'Participant');
  return {
    id: msg.id,
    senderId,
    senderName: senderId === currentUserId ? 'You' : senderName,
    message: msg.messageText || msg.message || '',
    timestamp: msg.createdAt || new Date().toISOString(),
    replyToMessage: msg.replyToMessage
      ? {
          id: msg.replyToMessage.id,
          messageText: msg.replyToMessage.messageText || msg.replyToMessage.message || '',
          senderId: msg.replyToMessage.senderId,
          senderName: (msg.replyToMessage?.sender?.displayName ?? msg.replyToMessage?.sender?.identifier ?? participantNames[msg.replyToMessage.senderId]) || (msg.replyToMessage.senderId === currentUserId ? 'You' : 'Participant'),
          createdAt: msg.replyToMessage.createdAt
        }
      : null,
    attachments: msg.attachments?.map((a: any) => ({
      id: a.id,
      fileType: a.fileType || 'document',
      fileUrl: a.fileUrl,
      fileName: a.fileName
    })),
    reactions: normalizeReactions(msg.reactions)
  };
}

class TournamentGroupChat extends Component<TournamentGroupChatProps, TournamentGroupChatState> {
  private messagesEndRef = React.createRef<HTMLDivElement>();
  private fileInputRef = React.createRef<HTMLInputElement>();
  private typingTimeout: ReturnType<typeof setTimeout> | null = null;
  private currentUserId: string | null = null;

  constructor(props: TournamentGroupChatProps) {
    super(props);
    this.state = {
      conversationId: null,
      messages: [],
      inputMessage: '',
      isLoading: true,
      isSending: false,
      isUploading: false,
      error: null,
      connectionStatus: 'connecting',
      chatAvailable: false,
      replyingTo: null,
      selectedFiles: [],
      typingUserIds: new Set(),
      participantNames: {}
    };
  }

  componentDidMount() {
    const tid = cleanUUID(this.props.tournamentId);
    if (!tid || !isValidUUID(tid)) {
      this.setState({ error: 'Invalid tournament.', isLoading: false });
      return;
    }
    this.currentUserId = getUserIdFromToken(apiService.getTokens().accessToken);
    this.openGroupChat();
  }

  componentWillUnmount() {
    this.removeSocketListeners();
    if (this.typingTimeout) clearTimeout(this.typingTimeout);
    const { conversationId } = this.state;
    if (conversationId) tournamentChatWebSocketService.leaveConversation(conversationId);
  }

  componentDidUpdate(_prev: TournamentGroupChatProps, prevState: TournamentGroupChatState) {
    if (this.state.messages.length !== prevState.messages.length) this.scrollToBottom();
  }

  openGroupChat = async () => {
    const { tournamentId } = this.props;
    const tid = cleanUUID(tournamentId);
    if (!tid) return;

    this.setState({ isLoading: true, error: null });

    try {
      const response = await apiService.getTournamentChatGroup(tid, { messageLimit: 50 });
      if (!(response as any).success) throw new Error((response as any).message || 'Failed to load group chat');

      const conversation = (response as any).conversation;
      const messages = (response as any).messages || [];
      if (!conversation?.id) {
        this.setState({ isLoading: false, chatAvailable: false, error: 'Could not open group chat.' });
        return;
      }

      const participantNames: Record<string, string> = {};
      (conversation.participants || []).forEach((p: any) => {
        const uid = p.userId || p.user?.id;
        if (uid) participantNames[uid] = p.user?.displayName || p.user?.identifier || p.user?.firstName || 'Participant';
      });

      const uiMessages: GroupMessage[] = (messages as any[])
        .map((m) => mapApiMessageToGroupMessage(m, this.currentUserId || '', participantNames))
        .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());

      this.setState({
        conversationId: conversation.id,
        messages: uiMessages,
        isLoading: false,
        chatAvailable: true,
        connectionStatus: 'connecting',
        participantNames
      });

      tournamentChatWebSocketService.connect(conversation.id);
      this.setupSocketListeners(conversation.id);

      try {
        await apiService.markTournamentChatAsRead(tid, conversation.id);
      } catch (_e) {}
    } catch (err: any) {
      const msg = err?.message || 'Failed to open group chat.';
      const is403 = /not registered|no longer available/i.test(msg);
      this.setState({
        isLoading: false,
        chatAvailable: false,
        error: is403 ? msg : 'You must be registered for this tournament and it must not be completed or cancelled.'
      });
    }
  };

  setupSocketListeners = (conversationId: string) => {
    tournamentChatWebSocketService.on('tournament_chat:joined', () => this.setState({ connectionStatus: 'connected' }));
    tournamentChatWebSocketService.on('tournament_chat:message', this.handleNewMessage);
    tournamentChatWebSocketService.on('tournament_chat:reaction:new', this.handleReactionNew);
    tournamentChatWebSocketService.on('tournament_chat:reaction:removed', this.handleReactionRemoved);
    tournamentChatWebSocketService.on('tournament_chat:typing', this.handleTyping);
    tournamentChatWebSocketService.on('disconnect', () => this.setState({ connectionStatus: 'disconnected' }));
    tournamentChatWebSocketService.on('error', () => this.setState({ connectionStatus: 'error' }));
  };

  removeSocketListeners = () => {
    tournamentChatWebSocketService.off('tournament_chat:joined', () => {});
    tournamentChatWebSocketService.off('tournament_chat:message', this.handleNewMessage);
    tournamentChatWebSocketService.off('tournament_chat:reaction:new', this.handleReactionNew);
    tournamentChatWebSocketService.off('tournament_chat:reaction:removed', this.handleReactionRemoved);
    tournamentChatWebSocketService.off('tournament_chat:typing', this.handleTyping);
  };

  handleNewMessage = (data: { conversationId: string; message?: any }) => {
    const { conversationId, participantNames } = this.state;
    if (data.conversationId !== conversationId || !data.message) return;
    const msg = data.message;
    const messageId = msg.id;
    if (!messageId) return;

    this.setState((prev) => {
      if (prev.messages.some((m) => m.id === messageId)) return prev;
      const isFromMe = (msg.senderId || msg.sender?.id) === this.currentUserId;
      if (isFromMe) {
        const tempIdx = prev.messages.findIndex((m) => m.id.startsWith('temp-'));
        if (tempIdx !== -1) {
          const updated = [...prev.messages];
          updated[tempIdx] = mapApiMessageToGroupMessage(msg, this.currentUserId || '', prev.participantNames);
          return { ...prev, messages: updated.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()) };
        }
      }
      const newMsg = mapApiMessageToGroupMessage(msg, this.currentUserId || '', prev.participantNames);
      const next = [...prev.messages, newMsg].sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
      return { ...prev, messages: next };
    });
  };

  handleReactionNew = (data: { conversationId: string; messageId?: string; reaction?: any }) => {
    const { conversationId } = this.state;
    if (data.conversationId !== conversationId || !data.messageId || !data.reaction) return;
    const { messageId, reaction } = data;
    const userId = reaction.userId || reaction.user_id;
    const type = reaction.reactionType;

    this.setState((prev) => ({
      ...prev,
      messages: prev.messages.map((m) => {
        if (m.id !== messageId) return m;
        const like = [...m.reactions.like].filter((id) => id !== userId);
        const dislike = [...m.reactions.dislike].filter((id) => id !== userId);
        if (type === 'like') like.push(userId);
        else if (type === 'dislike') dislike.push(userId);
        return { ...m, reactions: { like, dislike } };
      })
    }));
  };

  handleReactionRemoved = (data: { conversationId: string; messageId?: string; userId?: string }) => {
    const { conversationId } = this.state;
    if (data.conversationId !== conversationId || !data.messageId || !data.userId) return;

    this.setState((prev) => ({
      ...prev,
      messages: prev.messages.map((m) => {
        if (m.id !== data.messageId) return m;
        return {
          ...m,
          reactions: {
            like: m.reactions.like.filter((id) => id !== data.userId),
            dislike: m.reactions.dislike.filter((id) => id !== data.userId)
          }
        };
      })
    }));
  };

  handleTyping = (data: { conversationId: string; userId: string; isTyping: boolean }) => {
    const { conversationId } = this.state;
    if (data.conversationId !== conversationId) return;
    if (data.userId === this.currentUserId) return; // don't show own typing
    this.setState((prev) => {
      const next = new Set(prev.typingUserIds);
      if (data.isTyping) next.add(data.userId);
      else next.delete(data.userId);
      return { ...prev, typingUserIds: next };
    });
  };

  scrollToBottom = () => this.messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });

  scrollToMessage = (messageId: string) => {
    const el = document.querySelector(`[data-group-message-id="${messageId}"]`);
    if (el) {
      el.scrollIntoView({ behavior: 'auto', block: 'center' });
      el.classList.add(styles.highlightBlink);
      setTimeout(() => el.classList.remove(styles.highlightBlink), 650);
    }
  };

  loadMore = async () => {
    const { tournamentId } = this.props;
    const { conversationId, messages } = this.state;
    if (!conversationId || messages.length === 0) return;
    const oldestId = messages[0]?.id;
    if (!oldestId || oldestId.startsWith('temp-')) return;
    try {
      const res = await apiService.getTournamentChatMessages(cleanUUID(tournamentId)!, conversationId, {
        messageLimit: 50,
        beforeMessageId: oldestId
      });
      const list = (res as any).messages || [];
      if (list.length === 0) return;
      const older = list.map((m: any) =>
        mapApiMessageToGroupMessage(m, this.currentUserId || '', this.state.participantNames)
      );
      this.setState((prev) => ({
        ...prev,
        messages: [...older, ...prev.messages].sort(
          (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
        )
      }));
    } catch (_e) {}
  };

  handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    this.setState({ inputMessage: v });
    const { conversationId } = this.state;
    if (!conversationId) return;
    if (v.trim()) {
      tournamentChatWebSocketService.startTyping(conversationId);
      if (this.typingTimeout) clearTimeout(this.typingTimeout);
      this.typingTimeout = setTimeout(() => {
        tournamentChatWebSocketService.stopTyping(conversationId);
        this.typingTimeout = null;
      }, 2000);
    } else {
      tournamentChatWebSocketService.stopTyping(conversationId);
      if (this.typingTimeout) {
        clearTimeout(this.typingTimeout);
        this.typingTimeout = null;
      }
    }
  };

  handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    const { tournamentId } = this.props;
    const { conversationId, inputMessage, replyingTo, selectedFiles, isSending } = this.state;
    const tid = cleanUUID(tournamentId);
    if (!tid || !conversationId || isSending) return;
    if (!inputMessage.trim() && selectedFiles.length === 0) return;

    const text = inputMessage.trim() || '';
    const tempId = `temp-${Date.now()}`;
    const optimistic: GroupMessage = {
      id: tempId,
      senderId: this.currentUserId || '',
      senderName: 'You',
      message: text,
      timestamp: new Date().toISOString(),
      replyToMessage: replyingTo
        ? {
            id: replyingTo.id,
            messageText: replyingTo.message,
            senderId: replyingTo.senderId,
            senderName: replyingTo.senderName,
            createdAt: replyingTo.timestamp
          }
        : null,
      attachments: undefined,
      reactions: { like: [], dislike: [] }
    };

    this.setState((prev) => ({
      ...prev,
      messages: [...prev.messages, optimistic],
      inputMessage: '',
      isSending: true,
      isUploading: selectedFiles.length > 0,
      replyingTo: null
    }));
    tournamentChatWebSocketService.stopTyping(conversationId);
    if (this.typingTimeout) {
      clearTimeout(this.typingTimeout);
      this.typingTimeout = null;
    }

    try {
      const payload: { messageText?: string; replyToMessageId?: string; attachments?: File[] } = {};
      if (text) payload.messageText = text;
      if (replyingTo?.id && !replyingTo.id.startsWith('temp-')) payload.replyToMessageId = replyingTo.id;
      if (selectedFiles.length > 0) payload.attachments = selectedFiles;

      const response = await apiService.sendTournamentChatMessage(tid, conversationId, payload);
      const created = (response as any).message || (response as any).data?.message;
      if (created) {
        const newMsg = mapApiMessageToGroupMessage(created, this.currentUserId || '', this.state.participantNames);
        this.setState((prev) => ({
          ...prev,
          messages: prev.messages.map((m) => (m.id === tempId ? newMsg : m)),
          isSending: false,
          isUploading: false,
          selectedFiles: []
        }));
      } else {
        this.setState((prev) => ({ ...prev, isSending: false, isUploading: false, selectedFiles: [] }));
      }
      this.fileInputRef.current && (this.fileInputRef.current.value = '');
    } catch (err: any) {
      this.setState((prev) => ({
        ...prev,
        messages: prev.messages.filter((m) => m.id !== tempId),
        isSending: false,
        isUploading: false,
        error: err?.message || 'Failed to send.'
      }));
      setTimeout(() => this.setState((p) => ({ ...p, error: null })), 4000);
    }
  };

  handleReaction = async (messageId: string, reactionType: 'like' | 'dislike') => {
    const { tournamentId } = this.props;
    const { conversationId, messages } = this.state;
    if (!conversationId || !cleanUUID(tournamentId)) return;
    const uid = this.currentUserId;
    if (!uid) return;

    const msg = messages.find((m) => m.id === messageId);
    if (!msg) return;
    const current = msg.reactions.like.includes(uid) ? 'like' : msg.reactions.dislike.includes(uid) ? 'dislike' : null;

    if (current === reactionType) {
      try {
        await apiService.removeTournamentChatReaction(cleanUUID(tournamentId)!, conversationId, messageId);
      } catch (_e) {}
      return;
    }

    try {
      await apiService.addTournamentChatReaction(cleanUUID(tournamentId)!, conversationId, messageId, reactionType);
    } catch (_e) {}
  };

  formatTime = (ts: string) => new Date(ts).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });

  render() {
    const { tournamentName, participantCount, onClose } = this.props;
    const {
      conversationId,
      messages,
      inputMessage,
      isLoading,
      isSending,
      isUploading,
      error,
      connectionStatus,
      chatAvailable,
      replyingTo,
      selectedFiles,
      typingUserIds,
      participantNames
    } = this.state;
    const uid = this.currentUserId;

    if (!chatAvailable && !isLoading) {
      return (
        <div className={styles.container}>
          <div className={styles.header}>
            <h3>Group chat</h3>
            <button type="button" className={styles.closeBtn} onClick={onClose}>
              <X size={20} />
            </button>
          </div>
          <div className={styles.messagesArea}>
            <div className={styles.emptyState}>
              <p>{error || 'Group chat is not available.'}</p>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.headerInfo}>
            <div className={styles.headerAvatar}>👥</div>
            <div className={styles.headerText}>
              <h3>Group chat</h3>
              <div className={styles.headerSubtitle}>
                <span className={`${styles.connectionDot} ${styles[connectionStatus]}`} />
                {tournamentName}
                {participantCount != null && ` · ${participantCount} participants`}
              </div>
            </div>
          </div>
          <button type="button" className={styles.closeBtn} onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        {error && <div className={styles.errorBanner}>{error}</div>}

        <div className={styles.messagesArea}>
          {isLoading ? (
            <div className={styles.emptyState}>
              <div className={styles.loadingSpinner} />
              <p>Loading group chat…</p>
            </div>
          ) : !conversationId ? null : messages.length === 0 ? (
            <div className={styles.emptyState}>
              <p>No messages yet. Say hello to the group!</p>
            </div>
          ) : (
            <>
              {messages.map((msg) => {
                const isMine = msg.senderId === uid;
                const likeCount = msg.reactions.like.length;
                const dislikeCount = msg.reactions.dislike.length;
                const myLike = uid && msg.reactions.like.includes(uid);
                const myDislike = uid && msg.reactions.dislike.includes(uid);
                return (
                  <div
                    key={msg.id}
                    data-group-message-id={msg.id}
                    className={`${styles.bubble} ${isMine ? styles.bubbleMine : styles.bubbleTheirs}`}
                  >
                    <div className={styles.senderName}>{msg.senderName}</div>
                    {msg.replyToMessage && (
                      <div
                        className={styles.replyPreview}
                        onClick={() => this.scrollToMessage(msg.replyToMessage!.id)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => e.key === 'Enter' && this.scrollToMessage(msg.replyToMessage!.id)}
                      >
                        <div className={styles.replyPreviewSender}>{msg.replyToMessage.senderName}</div>
                        <div className={styles.replyPreviewText}>
                          {msg.replyToMessage.messageText.length > 50
                            ? `${msg.replyToMessage.messageText.slice(0, 50)}…`
                            : msg.replyToMessage.messageText}
                        </div>
                      </div>
                    )}
                    <div className={styles.messageBody}>{linkifyText(msg.message)}</div>
                    {msg.attachments?.length ? (
                      <div>
                        {msg.attachments.map((a) => {
                          const url = a.fileUrl?.trim().startsWith('http') ? a.fileUrl : (typeof window !== 'undefined' && window.location?.origin ? (a.fileUrl?.startsWith('/') ? window.location.origin + a.fileUrl : window.location.origin + '/' + a.fileUrl) : a.fileUrl);
                          const embedUrl = a.fileType === 'video' ? getVideoEmbedUrl(url) : null;
                          if (a.fileType === 'image') {
                            return (
                              <a key={a.id} href={url} target="_blank" rel="noopener noreferrer">
                                <img src={url} alt={a.fileName || 'Image'} style={{ maxWidth: '100%', borderRadius: 8 }} />
                              </a>
                            );
                          }
                          if (a.fileType === 'video' && embedUrl) {
                            return (
                              <div key={a.id} style={{ marginTop: 4 }}>
                                <iframe
                                  src={embedUrl}
                                  title={a.fileName || 'Video'}
                                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                  allowFullScreen
                                  style={{ width: '100%', maxWidth: '100%', aspectRatio: '16/9', maxHeight: 320, border: 'none', borderRadius: 8 }}
                                />
                              </div>
                            );
                          }
                          if (a.fileType === 'video') {
                            return (
                              <div key={a.id} style={{ marginTop: 4 }}>
                                <video controls playsInline preload="metadata" src={url} style={{ maxWidth: '100%', maxHeight: 320, borderRadius: 8 }} />
                              </div>
                            );
                          }
                          return (
                            <a key={a.id} href={url} target="_blank" rel="noopener noreferrer">
                              {a.fileName || 'File'}
                            </a>
                          );
                        })}
                      </div>
                    ) : null}
                    <div className={styles.bubbleFooter}>
                      <div className={styles.messageTime}>{this.formatTime(msg.timestamp)}</div>
                      <button
                        type="button"
                        className={styles.reactionBtn}
                        onClick={() => this.setState({ replyingTo: msg })}
                        title="Reply"
                      >
                        <Reply size={12} />
                      </button>
                      <div className={styles.reactionBar}>
                        <button
                          type="button"
                          className={`${styles.reactionBtn} ${myLike ? styles.activeLike : ''}`}
                          onClick={() => this.handleReaction(msg.id, 'like')}
                          title="Like"
                        >
                          <ThumbsUp size={14} />
                          {likeCount > 0 && <span>{likeCount}</span>}
                        </button>
                        <button
                          type="button"
                          className={`${styles.reactionBtn} ${myDislike ? styles.activeDislike : ''}`}
                          onClick={() => this.handleReaction(msg.id, 'dislike')}
                          title="Dislike"
                        >
                          <ThumbsDown size={14} />
                          {dislikeCount > 0 && <span>{dislikeCount}</span>}
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
              {typingUserIds.size > 0 && (
                <div className={styles.typingIndicator}>
                  <div className={styles.typingDots}>
                    <span />
                    <span />
                    <span />
                  </div>
                  <span>
                    {typingUserIds.size === 1 ? 'Someone is typing…' : `${typingUserIds.size} typing…`}
                  </span>
                </div>
              )}
            </>
          )}
          <div ref={this.messagesEndRef} />
        </div>

        {replyingTo && (
          <div className={styles.replyBar}>
            <span className={styles.replyBarText}>Replying to {replyingTo.senderName}: {replyingTo.message.slice(0, 40)}…</span>
            <button type="button" className={styles.cancelReplyBtn} onClick={() => this.setState({ replyingTo: null })}>
              <X size={16} />
            </button>
          </div>
        )}

        {selectedFiles.length > 0 && (
          <div className={styles.attachmentPreview}>
            {selectedFiles.length} file(s) selected
          </div>
        )}

        <form onSubmit={this.handleSend} className={styles.inputRow}>
          <input
            ref={this.fileInputRef}
            type="file"
            multiple
            accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.txt"
            onChange={(e) => {
              const files = e.target.files ? Array.from(e.target.files) : [];
              const valid = files.filter((f) => f.size <= 50 * 1024 * 1024).slice(0, 10);
              this.setState((prev) => ({ ...prev, selectedFiles: [...prev.selectedFiles, ...valid].slice(0, 10) }));
            }}
            style={{ display: 'none' }}
          />
          <button type="button" className={styles.attachBtn} onClick={() => this.fileInputRef.current?.click()}>
            <Paperclip size={20} />
          </button>
          <input
            type="text"
            value={inputMessage}
            onChange={this.handleInputChange}
            placeholder={replyingTo ? 'Reply…' : 'Message the group…'}
            className={styles.inputField}
            disabled={isSending}
          />
          <button
            type="submit"
            className={styles.sendBtn}
            disabled={(!inputMessage.trim() && selectedFiles.length === 0) || isSending || isUploading}
          >
            <Send size={20} />
          </button>
        </form>
      </div>
    );
  }
}

export default TournamentGroupChat;
